<template>
    <div class="station-info">
        <div class="address">
            <DSFontIcon code="pin" size="xxl" />
            <h4>{{ $t('contact') }}</h4>
            <p>
                <span>{{ station.name }}</span>
                <span>{{ station.street }}</span>
                <span>{{ station.postcode }} <span class="city">{{ station.city }}</span></span>
            </p>
        </div>
        <div class="call">
            <DSFontIcon code="call" size="xxl" />
            <h4>{{ $t('phoneNumber') }}</h4>
            <p>
                <a :href="`tel:${station.telephone}`">{{ station.telephone }}</a>
            </p>
        </div>
        <BlocksOpeningHours
            v-if="computeOpenHours"
            :name="$t('opens')"
            icon="clock"
            :opening-hours="computeOpenHours"
        />
        <BlocksOpeningHours
            v-if="computeOthersOpen('kitchen')"
            :name="$t('kitchen')"
            icon="restaurant"
            :opening-hours="computeOthersOpen('kitchen')"
        />
        <BlocksOpeningHours
            v-if="computeOthersOpen('lunch')"
            :name="$t('lunch')"
            icon="restaurant"
            :opening-hours="computeOthersOpen('lunch')"
        />
        <BlocksOpeningHours
            v-if="computeOthersOpen('breakfast')"
            :name="$t('breakfast')"
            icon="restaurant"
            :opening-hours="computeOthersOpen('breakfast')"
        />
        <div class="email">
            <DSFontIcon
                v-if="station.email"
                code="email"
                icon-style="line"
                size="xxl"
            />
            <p v-if="station.email">
                <span>{{ $t('email') }}</span>
                <strong><a :href="`mailto:${station.email}`">{{ station.email }}</a></strong>
            </p>
        </div>
    </div>
</template>
<script setup>
import { computed, useI18n } from '#imports';
import { sortBy } from 'lodash-es';
import { DateTime } from 'luxon';
const { t } = useI18n({
    useScope: 'global',
    messages: {
        'en-EU': {
            opens: 'Opening hours',
        },
        'fi-FI': {
            opens: 'Aukioloajat',
        },
        'sv-SE': {
            opens: 'Öppettider',
        },
        'no-NO': {
            opens: 'Åpningstider',
        },
    },
});

const props = defineProps({
    station: {
        type: Object,
        required: false,
        default: () => ({}),
    },
});

const computeOpenHours = computed(() => {
    const open24h = props.station.storeOpeningHours?.filter(item =>
        item?.openHours?.[0]?.startTime === '00:00:00'
        && item?.openHours?.[0]?.endTime === '24:00:00')?.length === 8
        || false;

    if (open24h) {
        return [{
            from: t('days.Mon'),
            to: t('days.Sun'),
            hours: [{
                startTime: '00:00:00',
                endTime: '24:00:00',
            }],
        }];
    }

    const storeOpeningHours = [...props.station.storeOpeningHours];

    storeOpeningHours.forEach((value, key) => {
        storeOpeningHours[key].day = Number(DateTime.fromString(value.date, 'yyyy-MM-dd').toFormat('c'));
    });

    const sortedOpenHours = sortBy(storeOpeningHours, ['day']);

    const openingHoursTable = [];
    let i = 0;

    sortedOpenHours.forEach((value, key) => {
        if (!openingHoursTable[i]) {
            openingHoursTable[i] = {};
        }
        if (!openingHoursTable[i].from) {
            openingHoursTable[i].from = t(`days.${DateTime.fromString(value.date, 'yyyy-MM-dd').toFormat('ccc')}`);
        }
        openingHoursTable[i].hours = value.openHours;

        if (!sortedOpenHours[key + 1] || JSON.stringify(value.openHours) !== JSON.stringify(sortedOpenHours[key + 1].openHours)) {
            openingHoursTable[i].to = t(`days.${DateTime.fromString(value.date, 'yyyy-MM-dd').toFormat('ccc')}`);
            i++;
        }
    });

    return openingHoursTable;
});

const computeOthersOpen = type => {
    const days = {
        0: 'Mon',
        1: 'Tue',
        2: 'Wed',
        3: 'Thu',
        4: 'Fri',
        5: 'Sat',
        6: 'Sun',
    };
    const typeHours = props.station.openingHours?.restaurantOpeningHours.find(v => v.type === type);

    if (typeHours) {
        let current;
        const openingHoursTable = [];

        typeHours.days.forEach((day, key) => {
            if (!current) {
                current = {
                    from: t(`days.${days[key]}`),
                };
            }

            if (JSON.stringify(typeHours.days[key]) !== JSON.stringify(typeHours.days[key + 1])) {
                current.to = t(`days.${days[key]}`);
                current.hours = day;
                openingHoursTable.push(current);
                current = null;
            }
        });

        return openingHoursTable;
    }

    return null;
};
</script>
<script>
export default {
    name: 'StationHeaderInfo',
    methods: {

    },
};

</script>
<style scoped>
.station-info {
  display: grid;
  margin: 2rem 0;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: 1.5rem;
  border-radius: 4px;
  position: relative;
  z-index: 1;
  background: var(--color-base-white);
}
.station-info .address,
.station-info .call,
.station-info .open-hours,
.station-info .email {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0 1rem;
}
.station-info .address .icon,
.station-info .call .icon,
.station-info .open-hours .icon,
.station-info .email .icon {
  grid-row-start: 1;
  grid-row-end: 3;
}
.station-info .address h4,
.station-info .call h4,
.station-info .open-hours h4,
.station-info .email h4 {
  grid-column-start: 2;
}
.station-info .address p,
.station-info .call p,
.station-info .open-hours p,
.station-info .email p {
  grid-column-start: 2;
}
.station-info .email,
.station-info .call {
  grid-row-start: 2;
}
.station-info .email span,
.station-info .call span {
  display: block;
}
.station-info .email {
  max-width: 100%;
  overflow: hidden;
  word-break: break-word;
}
.station-info .address strong, .station-info .address p > span {
  display: block;
}
@media (max-width: 1199px) {
  .station-info {
    grid-template-columns: repeat(2, 1fr);
    margin: 2rem 1rem;
  }
  .station-info .address p {
    grid-column-start: 2;
  }
  .station-info .call {
    grid-column-start: 1;
    grid-row-start: 2;
  }
  .station-info .call p {
    grid-column-start: 2;
    margin-top: 0;
  }
  .station-info .email {
    grid-column-start: 2;
    grid-row-start: 2;
  }
  .station-info .email p {
    grid-column-start: 2;
    margin-top: 0;
  }
}
@media (max-width: 991px) {
  .station-info {
    grid-template-columns: 1fr;
  }
  .station-info > * {
    grid-column-start: 1;
  }
  .station-info .icon {
    color: var(--color-base-grey-800);
  }
  .station-info > *:not(.link) {
    display: grid;
    grid-template-columns: 2rem 1fr;
  }
  .station-info > *:not(.link) a {
    text-decoration: none;
  }
  .station-info > *:not(.link) p {
    margin-top: 0;
  }
  .station-info > *:not(.link) p strong {
    margin-bottom: 0.25rem;
  }
  .station-info > *:not(.link) p > * {
    display: block;
  }
  .station-info .link a {
    color: var(--color-primary-900);
    font-weight: 700;
  }
  .station-info .link a:hover {
    text-decoration: underline;
  }
  .station-info .email {
    grid-column-start: 1;
    grid-row-start: auto;
  }
  .station-info .email p {
    grid-column-start: 2;
    margin-top: 0;
  }
}
</style>
